import { ContentLoading } from "../components/ContentLoading";
import { CustomerNoteCard } from "./CustomerNoteCard";
import { useMutation } from "@tanstack/react-query";
import { BookOpen, Plus, Search, Wrench } from "lucide-react";
import { useState, type SyntheticEvent } from "react";

import type { ArtifactScope } from "../../shared/customer-context-schemas";
import type { CustomerContextSummary } from "../../shared/customer-record-schemas";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { searchCustomerContext } from "./api";
import { useCustomerNotes } from "./useCustomerNotes";
import { RetrievalPageControls } from "./RetrievalPageControls";
import { CustomerArtifacts } from "./CustomerArtifacts";
import { AddContextItem } from "./CustomerContextForms";
import { OperationsLink } from "./OperationsLink";
import type { OperationsNavigate } from "./navigation";

function Empty({ children }: { children: string }) {
  return (
    <p className="rounded-sm border border-dashed p-3 text-sm text-muted-foreground">{children}</p>
  );
}

export function KnowledgeScopeNavigation({
  customerSlug,
  navigate,
  selectedToolId,
  tools,
}: {
  customerSlug: string;
  navigate: OperationsNavigate;
  selectedToolId?: string;
  tools: CustomerContextSummary["tools"];
}) {
  const scopes = [
    {
      href: `/customers/${customerSlug}/notes`,
      icon: BookOpen,
      key: "company",
      label: "Company-wide",
      selected: !selectedToolId,
    },
    ...tools.flatMap((tool) =>
      tool.platformToolId
        ? [
            {
              href: `/customers/${customerSlug}/tools/${tool.platformToolId}`,
              icon: Wrench,
              key: tool.platformToolId,
              label: tool.displayName,
              selected: selectedToolId === tool.platformToolId,
            },
          ]
        : [],
    ),
  ];
  return (
    <nav aria-labelledby="knowledge-scope-label">
      <p className="text-xs font-semibold text-muted-foreground" id="knowledge-scope-label">
        Knowledge scope
      </p>
      <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
        {scopes.map((scope) => {
          const Icon = scope.icon;
          return (
            <OperationsLink
              ariaCurrent={scope.selected ? "page" : undefined}
              className={`inline-flex min-h-10 shrink-0 items-center gap-2 rounded-sm border px-3 text-sm font-semibold no-underline transition-colors ${
                scope.selected
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-background text-foreground hover:border-primary hover:text-link"
              }`}
              href={scope.href}
              key={scope.key}
              navigate={navigate}
            >
              <Icon className="size-4" />
              {scope.label}
            </OperationsLink>
          );
        })}
      </div>
    </nav>
  );
}

export function CustomerSearch({
  organizationId,
  scope,
  title,
}: {
  organizationId: string;
  scope: ArtifactScope;
  title: string;
}) {
  const [query, setQuery] = useState("");
  const search = useMutation({
    mutationFn: () => searchCustomerContext(organizationId, { query, limit: 10, scope }),
  });
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    search.mutate();
  }
  return (
    <section aria-labelledby="knowledge-search-title" className="space-y-3">
      <div>
        <h3 className="text-balance text-base font-bold" id="knowledge-search-title">
          {title}
        </h3>
        <p className="mt-1 text-pretty text-sm text-muted-foreground">
          Find answers in the indexed files for this knowledge scope.
        </p>
      </div>
      <form className="flex flex-col gap-2 sm:flex-row" onSubmit={submit}>
        <Input
          aria-label={scope.kind === "tool" ? "Search this tool" : "Search customer-wide files"}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          placeholder={scope.kind === "tool" ? "Search this tool…" : "Search customer-wide files…"}
          minLength={2}
          required
        />
        <Button className="sm:w-auto" type="submit" disabled={search.isPending}>
          <Search /> {search.isPending ? "Searching…" : "Search"}
        </Button>
      </form>
      {search.error ? <FieldError>{search.error.message}</FieldError> : null}
      {search.data && search.data.coverage.state !== "ready" ? (
        <p role="status" className="rounded-sm border p-3 text-sm text-muted-foreground">
          {search.data.coverage.mode === "metadata"
            ? "Content search is unavailable. These results match titles and filenames only."
            : "Search coverage is incomplete. Some files may still be indexing or unavailable."}{" "}
          Use the file list below to browse all sources in this scope.
        </p>
      ) : null}
      {search.data?.results.map((result) => (
        <a
          className="block rounded-sm border p-3 transition-colors hover:border-primary"
          href={result.previewUrl}
          key={result.artifactId}
          rel="noreferrer"
          target="_blank"
        >
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold">{result.title}</p>
            <span className="shrink-0 text-xs font-semibold text-link">View source</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{result.filename}</p>
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{result.snippet}</p>
        </a>
      ))}
      {search.data?.results.length === 0 ? (
        <Empty>
          {search.data.coverage.state === "ready"
            ? "No matching indexed files."
            : "No matches in the sources currently searchable."}
        </Empty>
      ) : null}
    </section>
  );
}

function CustomerNotes({
  canWrite,
  organizationId,
}: {
  canWrite: boolean;
  organizationId: string;
}) {
  const [adding, setAdding] = useState(false);
  const notes = useCustomerNotes(organizationId, { kind: "customer" });
  const contextItems = notes.notes;
  return (
    <section aria-labelledby="customer-notes-title" className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-balance text-base font-bold" id="customer-notes-title">
            Notes
          </h3>
          <p className="mt-1 text-pretty text-sm text-muted-foreground">
            Add guidance, decisions, or company details that are not tied to one tool.
          </p>
        </div>
        {canWrite ? (
          <Button
            size="sm"
            type="button"
            variant="outline"
            onClick={() => {
              setAdding(true);
            }}
          >
            <Plus /> Add note
          </Button>
        ) : null}
      </div>
      {adding ? (
        <AddContextItem
          organizationId={organizationId}
          onAdded={() => {
            setAdding(false);
          }}
        />
      ) : null}
      {contextItems.map((item) => (
        <CustomerNoteCard
          key={item.id}
          note={item}
          organizationId={organizationId}
          canWrite={canWrite}
        />
      ))}
      <RetrievalPageControls
        label="notes"
        error={notes.error}
        pending={notes.isPending}
        fetching={notes.isFetchingNextPage}
        hasMore={notes.hasNextPage}
        loadMore={() => {
          void notes.fetchNextPage();
        }}
      />
      {!notes.isPending && !notes.error && contextItems.length === 0 ? (
        <Empty>No company-wide notes yet.</Empty>
      ) : null}
    </section>
  );
}

export function CustomerKnowledgeCard({
  canReadArtifacts,
  canReadContext,
  canSearch,
  canUploadArtifacts,
  canWriteContext,
  context,
  contextError,
  contextLoading,
  customerSlug,
  navigate,
  organizationId,
  ready,
}: {
  canReadArtifacts: boolean;
  canReadContext: boolean;
  canSearch: boolean;
  canUploadArtifacts: boolean;
  canWriteContext: boolean;
  context: CustomerContextSummary | null;
  contextError: Error | null;
  contextLoading: boolean;
  customerSlug: string;
  navigate: OperationsNavigate;
  organizationId: string;
  ready: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="size-4" /> Company-wide knowledge
        </CardTitle>
        <CardDescription className="text-pretty">
          Search and manage sources that apply across this customer. Choose a tool below for its
          dedicated knowledge.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-7">
        <KnowledgeScopeNavigation
          customerSlug={customerSlug}
          navigate={navigate}
          tools={context?.tools ?? []}
        />
        {contextLoading ? (
          <ContentLoading label="Loading knowledge scopes…" variant="cards" />
        ) : null}
        {contextError ? <FieldError>{contextError.message}</FieldError> : null}
        {canSearch ? (
          <CustomerSearch
            key={organizationId}
            organizationId={organizationId}
            scope={{ kind: "customer" }}
            title="Search company-wide knowledge"
          />
        ) : null}
        <div className="border-t pt-7">
          <CustomerArtifacts
            embedded
            organizationId={organizationId}
            ready={ready}
            canRead={canReadArtifacts}
            canUpload={canUploadArtifacts}
          />
        </div>
        {canReadContext && !contextLoading && !contextError ? (
          <div className="border-t pt-7">
            <CustomerNotes canWrite={canWriteContext} organizationId={organizationId} />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
